// dSpr Email - `/dSpr_EmailTemplates`
//
// Admin-only page. Three tabs (Templates / Workflow / CC Management) - only
// the Templates tab is wired today; the other two are scoped follow-ups and
// render a "Coming soon" placeholder.
//
// Templates tab:
//   • 6 cards, one per seeded dspr_email_templates row (DSP001..DSP006)
//   • Collapsed by default - header row only
//   • Click "Edit" → that one card expands inline with form
//   • Click "Preview" → opens a modal showing the rendered email
//
// Save path: PUT /api/dspr/email-templates/:id. The sender's 30s cache
// applies - edits land within 30 seconds of save.
//
// + New Cycle / + New Template buttons are HIDDEN per the spec - cycle
// creation lives on the Cycle Scheduler page; the dSpr template set is
// fixed at 6, with no in-product flow for creating new ones.
import React, { useEffect, useMemo, useState } from "react";
// Swal removed - themed alerts via useDsprAlerts() (see utils/dSprAlerts.js).
import { useDsprAlerts } from "./utils/dSprAlerts";
import { fmtDateTimeIST } from "./utils/dSpr_DateFormat";
import { useNavigate } from "react-router-dom";
import { FaRegEye, FaFileAlt, FaPaperPlane, FaLock, FaSyncAlt } from "react-icons/fa";
import Select from "react-select";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";

// Per-template visual accent (colored dot + matching type pill background).
// Indexed by the DB `type` column. Hex matches the user's spec exactly.
const TEMPLATE_ACCENT = {
    request_review:   { dot: "#10B981", pillBg: "#d1fae5", pillFg: "#065f46" },
    reminder_1:       { dot: "#F59E0B", pillBg: "#fef3c7", pillFg: "#92400e" },
    reminder_2:       { dot: "#8B5CF6", pillBg: "#ede9fe", pillFg: "#5b21b6" },
    confirmation:     { dot: "#3B82F6", pillBg: "#dbeafe", pillFg: "#1e40af" },
    confirmation_pdf: { dot: "#8B5CF6", pillBg: "#ede9fe", pillFg: "#5b21b6" },
};
const DEFAULT_ACCENT = { dot: "#94a3b8", pillBg: "#f1f5f9", pillFg: "#475569" };

// Merge fields shown as reference text under "EMAIL BODY". Inline grey text
// (NOT clickable chips - that was the old design). HR can copy/paste these
// into the textarea as needed.
const MERGE_FIELDS = ["{{employee_name}}", "{{half}}", "{{deadline}}", "{{review_link}}"];

// fmtDateTimeIST now lives in src/utils/dSpr_DateFormat.js - imported above.

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
function DSprEmailTemplates({ navSize }) {
    const navigate = useNavigate();
    const { showAlert, AlertsHost } = useDsprAlerts();

    const [activeTab, setActiveTab] = useState("templates");
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    // editing: { [templateId]: { subject, body, error, saving } }
    const [editing, setEditing] = useState({});

    // Preview modal state - the template id being previewed (null = closed)
    const [previewTemplate, setPreviewTemplate] = useState(null);

    // Optional: the latest cycle's half + deadline, used to pre-fill preview
    // vars with representative values. Static fallback if no cycle found.
    const [previewVars, setPreviewVars] = useState({
        half: "H1 2026",
        deadline: "2026-06-30",
    });

    // ── Auth + initial load ──────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/employee/logined_employee", { credentials: "include" });
                const data = await res.json();
                if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                    navigate("/login"); return;
                }
                const me = Array.isArray(data) ? data[0] : data;
                if (!me?.emp_id) { navigate("/login"); return; }
                // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
            } catch { navigate("/login"); return; }
            await Promise.all([fetchTemplates(), fetchPreviewVarsFromActiveCycle()]);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/dspr/email-templates", { credentials: "include" });
            const list = await res.json();
            if (!res.ok) throw new Error(list.error || "Failed to load templates");
            setTemplates(Array.isArray(list) ? list : []);
        } catch (e) {
            showAlert("error", "Couldn't load templates", e.message);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPreviewVarsFromActiveCycle = async () => {
        // Best-effort: fill {{half}} + {{deadline}} from the current Active
        // cycle. If the call fails OR there's no Active cycle, fall back to
        // the static defaults already in state.
        try {
            const res = await apiFetch("/api/dspr/cycles", { credentials: "include" });
            const list = await res.json();
            if (!res.ok || !Array.isArray(list)) return;
            const active = list.find(c => c.status === "Active") || list[0];
            if (active) {
                setPreviewVars({
                    half: active.half_label || "H1 2026",
                    deadline: active.end_date || "2026-06-30",
                });
            }
        } catch { /* silent - defaults remain */ }
    };

    // ── Edit lifecycle ───────────────────────────────────────────────────────
    const startEdit = (tpl) => {
        if (editing[tpl.id]) return;
        setEditing(prev => ({
            ...prev,
            [tpl.id]: { subject: tpl.subject, body: tpl.body, error: null, saving: false },
        }));
    };
    const cancelEdit = (id) => {
        setEditing(prev => {
            const { [id]: _, ...rest } = prev;
            return rest;
        });
    };
    const updateField = (id, field, value) => {
        setEditing(prev => ({ ...prev, [id]: { ...prev[id], [field]: value, error: null } }));
    };
    const saveEdit = async (id) => {
        const draft = editing[id];
        if (!draft) return;
        setEditing(prev => ({ ...prev, [id]: { ...prev[id], saving: true, error: null } }));
        try {
            const res = await apiFetch(`/api/dspr/email-templates/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ subject: draft.subject, body: draft.body }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Save failed");
            setTemplates(prev => prev.map(t => t.id === id ? data.template : t));
            cancelEdit(id);
            showAlert("success", "Template saved");
        } catch (e) {
            setEditing(prev => ({ ...prev, [id]: { ...prev[id], saving: false, error: e.message } }));
        }
    };

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                {/* Mounted INSIDE the dashboard - was previously a flex-row
                    sibling, which caused a horizontal jump on each alert. */}
                <AlertsHost />
                {/* ════════════════════════════════════════════════════════════
                    REGION 1 - Page header
                    "Email" (short), subtitle, no + New Cycle button per spec.
                    ════════════════════════════════════════════════════════════ */}
                {/* No dspr-em-region here — that class adds a white card +
                    dashed border, which clashes with the Support_Tickets-
                    style header treatment used elsewhere in dAdmin. The
                    region treatment stays on the body blocks below. */}
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        SPR - Email Templates
                    </h2>
                    <p className="dspr-subtitle">
                        Templates and SPR email content management.
                    </p>
                </div>

                <div className="dspr-dashboard-content">
                    {/* ════════════════════════════════════════════════════════
                        REGION 2 - Tab row
                        Pill-shaped tabs. Active tab = solid orange.
                        ════════════════════════════════════════════════════════ */}
                    <div className="dspr-em-region dspr-em-tabs">
                        <button type="button"
                            className={`dspr-em-tab ${activeTab === "templates" ? "active" : ""}`}
                            onClick={() => setActiveTab("templates")}>
                            <FaFileAlt /> Templates
                        </button>
                        <button type="button"
                            className={`dspr-em-tab ${activeTab === "workflow" ? "active" : ""}`}
                            onClick={() => setActiveTab("workflow")}>
                            <FaPaperPlane /> Workflow
                        </button>
                        <button type="button"
                            className={`dspr-em-tab ${activeTab === "cc" ? "active" : ""}`}
                            onClick={() => setActiveTab("cc")}>
                            <FaLock /> CC Management
                        </button>
                    </div>

                    {/* ════════════════════════════════════════════════════════
                        REGION 3 - Tab content
                        ════════════════════════════════════════════════════════ */}
                    {activeTab === "templates" && (
                        <TemplatesTab
                            templates={templates}
                            loading={loading}
                            editing={editing}
                            onStartEdit={startEdit}
                            onCancelEdit={cancelEdit}
                            onSaveEdit={saveEdit}
                            onUpdateField={updateField}
                            onPreview={(tpl) => setPreviewTemplate(tpl)}
                        />
                    )}
                    {activeTab === "workflow" && <WorkflowTab />}
                    {activeTab === "cc" && <ComingSoon name="CC Management" />}
                </div>
            </div>

            {previewTemplate && (
                <TemplatePreviewModal
                    template={previewTemplate}
                    vars={previewVars}
                    onClose={() => setPreviewTemplate(null)}
                />
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// TEMPLATES TAB
// ════════════════════════════════════════════════════════════════════════════
function TemplatesTab({ templates, loading, editing, onStartEdit, onCancelEdit, onSaveEdit, onUpdateField, onPreview }) {
    return (
        <>
            {/* Section header - "Email Templates" + count. + New Template button hidden per spec. */}
            <div className="dspr-em-region dspr-em-section-head">
                <div>
                    <div className="dspr-em-section-title">Email templates</div>
                    <div className="dspr-em-section-sub">
                        {templates.length} template{templates.length !== 1 ? "s" : ""} · used for all outgoing self-review emails
                    </div>
                </div>
                {/* + New Template intentionally hidden - dSpr ships a fixed seeded set */}
            </div>

            {/* List of template cards */}
            <div className="dspr-em-region dspr-em-list">
                {loading ? (
                    <div className="dspr-empty">Loading templates…</div>
                ) : templates.length === 0 ? (
                    <div className="dspr-empty">No templates found. Check that dspr_email_templates is seeded.</div>
                ) : (
                    templates.map(tpl => (
                        <TemplateRow
                            key={tpl.id}
                            tpl={tpl}
                            draft={editing[tpl.id]}
                            onStartEdit={() => onStartEdit(tpl)}
                            onCancelEdit={() => onCancelEdit(tpl.id)}
                            onSaveEdit={() => onSaveEdit(tpl.id)}
                            onUpdateField={(field, val) => onUpdateField(tpl.id, field, val)}
                            onPreview={() => onPreview(tpl)}
                        />
                    ))
                )}
            </div>
        </>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// TEMPLATE ROW - collapsed header + inline edit form
// ════════════════════════════════════════════════════════════════════════════
function TemplateRow({ tpl, draft, onStartEdit, onCancelEdit, onSaveEdit, onUpdateField, onPreview }) {
    const accent = TEMPLATE_ACCENT[tpl.type] || DEFAULT_ACCENT;
    const isEditing = !!draft;

    return (
        <div className={`dspr-em-tpl-row dspr-em-region ${isEditing ? "editing" : ""}`}>
            {/* Header - always visible (collapsed and edit state both) */}
            <div className="dspr-em-tpl-head">
                {/* Left-edge accent dot */}
                <span className="dspr-em-tpl-dot" style={{ background: accent.dot }} />

                {/* Identity column */}
                <div className="dspr-em-tpl-identity">
                    <div className="dspr-em-tpl-name-row">
                        <strong>{tpl.name}</strong>
                        <span className="dspr-em-tpl-type-pill"
                            style={{ background: accent.pillBg, color: accent.pillFg }}>
                            {tpl.type.toUpperCase()}
                        </span>
                    </div>
                    <div className="dspr-em-tpl-subject-line">
                        <strong>Subject:</strong> {isEditing ? draft.subject : tpl.subject}
                    </div>
                </div>

                {/* Actions - right side */}
                <div className="dspr-em-tpl-actions">
                    <button type="button" className="dspr-em-preview-btn" onClick={onPreview}>
                        <FaRegEye /> Preview
                    </button>
                    {!isEditing ? (
                        <button type="button" className="dspr-em-edit-btn" onClick={onStartEdit}>
                            Edit
                        </button>
                    ) : (
                        <>
                            <button type="button" className="dspr-em-save-btn" onClick={onSaveEdit} disabled={draft.saving}>
                                {draft.saving ? "Saving…" : "Save"}
                            </button>
                            <button type="button" className="dspr-em-cancel-btn" onClick={onCancelEdit} disabled={draft.saving}>
                                Cancel
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Edit body - only when isEditing */}
            {isEditing && (
                <div className="dspr-em-tpl-edit-body">
                    {/* TEMPLATE NAME - read-only for system templates */}
                    <div className="dspr-em-field">
                        <label>TEMPLATE NAME</label>
                        <input type="text" className="dspr-em-input" value={tpl.name} readOnly disabled />
                    </div>

                    {/* SUBJECT LINE - editable */}
                    <div className="dspr-em-field">
                        <label>SUBJECT LINE</label>
                        <input
                            type="text"
                            className="dspr-em-input"
                            value={draft.subject}
                            onChange={e => onUpdateField("subject", e.target.value)}
                        />
                    </div>

                    {/* EMAIL BODY + inline merge fields reference */}
                    <div className="dspr-em-field">
                        <div className="dspr-em-body-label-row">
                            <label>EMAIL BODY</label>
                            <span className="dspr-em-merge-hint">
                                AVAILABLE MERGE FIELDS:{" "}
                                {MERGE_FIELDS.map((m, i) => (
                                    <code key={m} className="dspr-em-merge-code">
                                        {m}{i < MERGE_FIELDS.length - 1 ? " " : ""}
                                    </code>
                                ))}
                            </span>
                        </div>
                        <textarea
                            className="dspr-em-textarea"
                            rows={15}
                            value={draft.body}
                            onChange={e => onUpdateField("body", e.target.value)}
                        />
                        {draft.error && (
                            <div className="dspr-tpl-error">⚠ {draft.error}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// WORKFLOW TAB
//   Read-only audit trail per cycle. Cycle dropdown + manager-grouped cards.
//   Each employee row → 4 status pills (Request / R1 / R2 / R3) + 4 detail
//   boxes underneath showing timestamps or "Not sent yet".
//
//   Owns its own data: fetches /api/dspr/cycles + /api/dspr/workflow/by-cycle.
//   Refresh button next to the dropdown re-fetches the current cycle.
// ════════════════════════════════════════════════════════════════════════════

// Deterministic color picker for the manager-group dot. Same manager_id →
// same color every render. Independent of Cycle Scheduler's manager_dot
// (intentional - different visual contexts).
const MGR_PALETTE = ["#E8520A", "#3b82f6", "#8b5cf6", "#10b981", "#ef4444", "#06b6d4"];
function managerHashColor(managerId) {
    if (!managerId) return "#94a3b8";
    let sum = 0;
    for (let i = 0; i < managerId.length; i++) sum += managerId.charCodeAt(i);
    return MGR_PALETTE[sum % MGR_PALETTE.length];
}

// Per-kind visual metadata for the pills + detail boxes.
// Pill colour AND box-left-border colour kept consistent per kind.
const WF_KIND_META = {
    request:   { label: "Request review", boxLabel: "REQUEST REVIEW", icon: "✉", color: "#10b981", pillBg: "#d1fae5", pillFg: "#065f46" },
    reminder1: { label: "Reminder 1",     boxLabel: "REMINDER 1",     icon: "⌛", color: "#f59e0b", pillBg: "#fef3c7", pillFg: "#92400e" },
    reminder2: { label: "Reminder 2",     boxLabel: "REMINDER 2",     icon: "⌛", color: "#f59e0b", pillBg: "#fef3c7", pillFg: "#92400e" },
};

function WorkflowTab() {
    const [cycles, setCycles] = useState([]);
    const [selectedCycleId, setSelectedCycleId] = useState(null);
    const [workflow, setWorkflow] = useState(null);   // { cycle_id, managers: [...] }
    const [loadingCycles, setLoadingCycles] = useState(true);
    const [loadingWorkflow, setLoadingWorkflow] = useState(false);
    const [error, setError] = useState(null);

    // ── Initial: fetch the cycle list, pick a default ──────────────────────
    useEffect(() => {
        (async () => {
            setLoadingCycles(true);
            try {
                const res = await apiFetch("/api/dspr/cycles", { credentials: "include" });
                const list = await res.json();
                const cs = Array.isArray(list) ? list : [];
                setCycles(cs);
                // Default: Active cycle, else newest
                const target = cs.find(c => c.status === "Active") || cs[0];
                if (target) setSelectedCycleId(target.id);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoadingCycles(false);
            }
        })();
    }, []);

    // ── Fetch workflow data when cycle changes ─────────────────────────────
    const fetchWorkflow = async () => {
        if (!selectedCycleId) return;
        setLoadingWorkflow(true);
        setError(null);
        try {
            const res = await apiFetch(`/api/dspr/workflow/by-cycle/${selectedCycleId}`, { credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Couldn't load workflow");
            setWorkflow(data);
        } catch (e) {
            setError(e.message);
            setWorkflow(null);
        } finally {
            setLoadingWorkflow(false);
        }
    };
    useEffect(() => { fetchWorkflow(); /* eslint-disable-next-line */ }, [selectedCycleId]);

    // ── Dropdown options (same sort as Review Management) ──────────────────
    const cycleOptions = useMemo(() => {
        const order = { Active: 0, Scheduled: 1, Closed: 2 };
        return cycles
            .slice()
            .sort((a, b) => {
                if (a.year !== b.year) return b.year - a.year;
                if (a.half_num !== b.half_num) return b.half_num - a.half_num;
                return (order[a.status] ?? 9) - (order[b.status] ?? 9);
            })
            .map(c => ({ value: c.id, label: `${c.half_label} - ${c.status}` }));
    }, [cycles]);

    const selectedOption = cycleOptions.find(o => o.value === selectedCycleId) || null;
    const managers = workflow?.managers || [];

    return (
        <>
            {/* Header - title + subtitle + cycle dropdown + refresh */}
            <div className="dspr-em-region dspr-wf-header">
                <div className="dspr-wf-title">Email workflow</div>
                <div className="dspr-wf-subtitle">Full audit trail - per cycle, per manager, per employee</div>
                <div className="dspr-wf-cycle-row">
                    <label className="dspr-wf-cycle-label">Cycle:</label>
                    <div className="dspr-wf-cycle-select">
                        <Select
                            options={cycleOptions}
                            value={selectedOption}
                            isLoading={loadingCycles}
                            isDisabled={loadingCycles}
                            onChange={(opt) => setSelectedCycleId(opt?.value || null)}
                            isSearchable={false}
                            placeholder="Select a cycle…"
                        />
                    </div>
                    <button type="button"
                        className="dspr-wf-refresh"
                        onClick={fetchWorkflow}
                        disabled={!selectedCycleId || loadingWorkflow}
                        title="Refresh workflow data for the selected cycle">
                        <FaSyncAlt className={loadingWorkflow ? "dspr-wf-refresh-spinning" : ""} />
                    </button>
                </div>
            </div>

            {/* Body - loading / empty / managers list */}
            {loadingWorkflow ? (
                <div className="dspr-empty">Loading workflow…</div>
            ) : error ? (
                <div className="dspr-tpl-error">⚠ {error}</div>
            ) : !selectedCycleId ? (
                <div className="dspr-empty">Select a cycle above to view its workflow.</div>
            ) : managers.length === 0 ? (
                <div className="dspr-empty">
                    No employees allocated to this cycle yet. Allocate employees via the Manager Allocation page.
                </div>
            ) : (
                managers.map(g => <WorkflowManagerGroup key={g.manager_id || "_none_"} group={g} />)
            )}
        </>
    );
}

// ── Manager group card - header + employee rows ────────────────────────────
function WorkflowManagerGroup({ group }) {
    const managerName = `${group.manager_first_name || ""} ${group.manager_last_name || ""}`.trim() || group.manager_id || "(no manager)";
    const dotColor = managerHashColor(group.manager_id);
    const empCount = group.employees.length;

    return (
        <div className="dspr-em-region dspr-wf-mgr-card">
            <div className="dspr-wf-mgr-head">
                <span className="dspr-wf-mgr-dot" style={{ background: dotColor }} />
                <strong className="dspr-wf-mgr-name">{managerName}</strong>
                <span className="dspr-wf-emp-count">{empCount} employee{empCount !== 1 ? "s" : ""}</span>
            </div>
            <div className="dspr-wf-emp-list">
                {group.employees.map((emp, i) => (
                    <React.Fragment key={emp.employee_id}>
                        <WorkflowEmployeeRow emp={emp} />
                        {i < group.employees.length - 1 && <hr className="dspr-wf-emp-divider" />}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

// ── Single employee row - header + 4 status pills + 4 detail boxes ─────────
function WorkflowEmployeeRow({ emp }) {
    const fullName = `${emp.employee_first_name || ""} ${emp.employee_last_name || ""}`.trim() || emp.employee_id;
    const initials = `${(emp.employee_first_name || "?").charAt(0)}${(emp.employee_last_name || "").charAt(0)}`.toUpperCase();
    const state = emp.email_state || {};
    const atFor = {
        request:   state.request_at,
        reminder1: state.reminder1_at,
        reminder2: state.reminder2_at,
    };

    return (
        <div className="dspr-wf-emp-row">
            <div className="dspr-wf-emp-head">
                <div className="dspr-wf-emp-avatar">{initials}</div>
                <div className="dspr-wf-emp-identity">
                    <strong>{fullName}</strong>
                    <div className="dspr-wf-emp-email">{emp.employee_email || "-"}</div>
                </div>
                <div className="dspr-wf-pill-row">
                    {["request", "reminder1", "reminder2"].map(kind => (
                        <WorkflowStatusPill key={kind} kind={kind} at={atFor[kind]} />
                    ))}
                </div>
            </div>
            <div className="dspr-wf-detail-grid">
                {["request", "reminder1", "reminder2"].map(kind => (
                    <WorkflowDetailBox key={kind} kind={kind} at={atFor[kind]} />
                ))}
            </div>
        </div>
    );
}

// ── Status pill - small right-aligned chip ─────────────────────────────────
function WorkflowStatusPill({ kind, at }) {
    const meta = WF_KIND_META[kind];
    const sent = !!at;
    const tooltip = sent
        ? `${meta.label} sent on ${fmtDateTimeIST(at)} IST`
        : `${meta.label} not yet sent`;
    return (
        <span
            className={`dspr-wf-pill ${sent ? "sent" : ""}`}
            style={sent ? { background: meta.pillBg, color: meta.pillFg, borderColor: meta.color } : {}}
            title={tooltip}>
            <span className="dspr-wf-pill-glyph">{sent ? "✓" : "⊙"}</span>
            {meta.label}
        </span>
    );
}

// ── Detail box - uppercase label + timestamp or "Not sent yet" ─────────────
function WorkflowDetailBox({ kind, at }) {
    const meta = WF_KIND_META[kind];
    const sent = !!at;
    const tooltip = sent
        ? `${meta.label} dispatched at ${fmtDateTimeIST(at)} IST`
        : `${meta.label} not yet sent. Trigger via Cycle Scheduler or Review Management.`;
    return (
        <div
            className={`dspr-wf-box ${sent ? "sent" : "pending"}`}
            style={sent ? { borderLeftColor: meta.color } : {}}
            title={tooltip}>
            <div className="dspr-wf-box-head">
                <span className="dspr-wf-box-icon" style={sent ? { color: meta.color } : {}}>{meta.icon}</span>
                <span className="dspr-wf-box-label">{meta.boxLabel}</span>
            </div>
            <div className="dspr-wf-box-value">
                {sent ? fmtDateTimeIST(at) : "Not sent yet"}
            </div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// COMING SOON - placeholder for Workflow + CC Management tabs
// ════════════════════════════════════════════════════════════════════════════
function ComingSoon({ name }) {
    return (
        <div className="dspr-em-region dspr-em-coming-soon">
            <div className="dspr-em-cs-icon">🚧</div>
            <div className="dspr-em-cs-title">{name}</div>
            <div className="dspr-em-cs-sub">This tab is under construction. Coming soon.</div>
        </div>
    );
}

// ════════════════════════════════════════════════════════════════════════════
// TEMPLATE PREVIEW MODAL
//   Header:  "<Template Name>" + "Email Template Preview"
//   Subject card + body iframe (rendered server-side, same path as the live
//   preview from Cycle Scheduler - POST /api/dspr/email-templates/preview).
//   Footer:  green info banner + Close button.
// ════════════════════════════════════════════════════════════════════════════
function TemplatePreviewModal({ template, vars, onClose }) {
    const [rendered, setRendered] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true); setError(null);
            try {
                // No cycle_id - backend auto-falls-back to the most recent
                // Active cycle to populate {{half}}, {{deadline}}, etc. with
                // realistic dates. Per-type deadline (R1/R2/R3/end+90) is
                // resolved server-side via computeDeadlineForType.
                const previewVars = {
                    employee_name: "<Recipient Name>",
                    review_link: "<Personalized self-review link>",
                };
                const res = await apiFetch("/api/dspr/email-templates/preview", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ type: template.type, vars: previewVars }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || "Couldn't load preview");
                setRendered(data);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="dspr-modal-overlay">
            <div className="dspr-modal dspr-em-preview-modal" onClick={e => e.stopPropagation()}>
                <div className="dspr-modal-head dspr-em-preview-head">
                    <div className="dspr-em-preview-head-text">
                        <div className="dspr-em-preview-title">{template.name}</div>
                        <div className="dspr-em-preview-subtitle">Email Template Preview</div>
                    </div>
                    <button className="dspr-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="dspr-modal-body">
                    {/* Subject card */}
                    <div className="dspr-em-preview-subject-card">
                        <div className="dspr-em-preview-sub-label">SUBJECT</div>
                        <div className="dspr-em-preview-sub-value">
                            {loading ? "Loading…" : error ? "-" : rendered?.subject || "-"}
                        </div>
                    </div>

                    {/* Body iframe */}
                    <div className="dspr-preview-iframe-wrap">
                        {loading ? (
                            <div className="dspr-empty" style={{ padding: 30 }}>Loading preview…</div>
                        ) : error ? (
                            <div className="dspr-tpl-error">⚠ {error}</div>
                        ) : (
                            <iframe
                                className="dspr-preview-iframe"
                                srcDoc={rendered.html}
                                title="Email Template Preview"
                                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                            />
                        )}
                    </div>

                    {/* CC info banner (green, no Send button - this is a template preview) */}
                    <div className="dspr-em-preview-cc-banner">
                        ✓ CC list from CC Management will be automatically included on all sent emails.
                    </div>
                </div>

                <div className="dspr-modal-foot dspr-em-preview-foot">
                    <button type="button" className="dspr-em-close-btn" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DSprEmailTemplates;
